/**
 * O painel *Envios ao IW* do detalhe do Caso (NR-255). Só aparece quando o Caso já mandou algo ao IW.
 *
 * Cada envio diz em texto onde está: na fila (o IW não respondeu, e a plataforma refaz sozinha, sem duplicar), gravado
 * (com o id do IW), recusado (com o campo e o motivo do IW) ou esgotado. Tipo ou estado que a tela não conhece aparece
 * cru — sinalizado, nunca some.
 */

import { useQuery } from '@tanstack/react-query';

import { formatarDataHora } from '../../shared/dominio/datas';
import { Painel } from '../../shared/ui/primitivas';
import { ROTULOS_DO_CAMPO_DO_IW } from './api/cadastroNoIw';
import { opcoesDosEnviosAoIw, type EnvioAoIw } from './api/enviosAoIw';

const ROTULOS_DO_TIPO: Readonly<Record<string, string>> = {
  CreatePatient: 'Cadastro do paciente',
  OpenAdmission: 'Abertura da admissão',
  UpdateAdmission: 'Atualização da admissão',
  CreateBudget: 'Gravação do orçamento',
  UpdateBudget: 'Edição do orçamento',
};

function tentativas(n: number): string {
  return n === 1 ? '1 tentativa' : `${String(n)} tentativas`;
}

function situacao(envio: EnvioAoIw): string {
  switch (envio.estado) {
    case 'Pending':
      return `Na fila: o IW ainda não respondeu. ${tentativas(envio.tentativas)}; a próxima é refeita sozinha.`;
    case 'Completed':
      return envio.idNoIw !== null ? `Gravado no IW (id ${envio.idNoIw}).` : 'Gravado no IW.';
    case 'Refused': {
      const campos = envio.camposRecusados.map((c) => ROTULOS_DO_CAMPO_DO_IW[c] ?? c).join(', ');
      const partes = [campos === '' ? null : `${campos}.`, envio.motivo].filter((p): p is string => p !== null && p !== '');
      const quem = envio.recusadoPelaPlataforma ? 'Parado pela plataforma' : 'Recusado pelo IW';
      return partes.length === 0 ? `${quem}.` : `${quem}: ${partes.join(' ')}`;
    }
    case 'Exhausted':
      return `O IW não respondeu em nenhuma das ${String(envio.tentativas)} tentativas. Envie de novo quando ele voltar.`;
    default:
      return `Situação ${envio.estado} (esta tela ainda não a conhece).`;
  }
}

export function EnviosAoIw({ casoId }: { casoId: string }): React.JSX.Element | null {
  const envios = useQuery(opcoesDosEnviosAoIw(casoId));

  // Sem envio, não há o que mostrar. Falha na leitura não esconde o resto do Caso: o painel some e o detalhe segue.
  if (!envios.isSuccess || envios.data.length === 0) return null;

  return (
    <Painel titulo="Envios ao IW">
      <ul className="list-none space-y-2 pl-0">
        {envios.data.map((envio) => (
          <li key={envio.id} className="text-[0.8125rem]">
            <span className="block font-medium">{ROTULOS_DO_TIPO[envio.tipo] ?? envio.tipo}</span>
            <span className="block">{situacao(envio)}</span>
            <span className="num block text-xs text-[var(--tinta-2)]">Pedido em {formatarDataHora(envio.pedidoEm)}</span>
          </li>
        ))}
      </ul>
    </Painel>
  );
}
